/**
 * Leviathan - Tax configuration readiness
 * Quasar Prime LLC
 *
 * One screen answering: is this instance configured to calculate tax properly?
 *
 * Tax correctness depends on half a dozen separate things being set up, in
 * different screens, by different people, at different times. Any one missing
 * produces a WRONG NUMBER rather than an error, and most are invisible until a
 * return is reconciled. This is the onboarding screen, and the one a customer's
 * admin returns to after a legislative session.
 *
 * THE LIMITATIONS PANEL IS NOT DECORATION. A green check here says the
 * configuration is PRESENT. It cannot say a rate is CORRECT — an instance whose
 * Texas diesel rate was typed as 0.02 instead of 0.20 passes every check on
 * this page. Reading green as "my tax figures are right" is the single most
 * damaging misunderstanding this screen could create, so the limitations are
 * rendered at the top, unconditionally, and never behind a collapse.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  RefreshCw,
  ShieldQuestion,
} from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { API_URL } from '@/config/api';
import { parseApiError } from '@/utils/errorHandler';

interface ReadinessCheck {
  id: string;
  title: string;
  severity: 'BLOCKER' | 'WARNING' | 'INFO';
  count: number;
  consequence: string;
  fixPath: string;
  fixLabel: string;
  examples: { id: string; label: string; detail?: string }[];
}

interface ReadinessReport {
  ready: boolean;
  checks: ReadinessCheck[];
  blockers: number;
  warnings: number;
  limitations: string[];
  generatedAt: string;
}

export default function TaxReadiness() {
  const [report, setReport] = useState<ReadinessReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/tax/config/readiness`);
      if (!response.ok) throw new Error(await parseApiError(response));
      setReport(await response.json());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run the readiness check');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/50 p-4 text-red-300">
          {error ?? 'No report'}
        </div>
      </div>
    );
  }

  const passed = report.checks.filter((c) => c.count === 0);
  const failed = report.checks.filter((c) => c.count > 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Tax Configuration Readiness</h1>
          <p className="text-slate-400 text-sm mt-1">
            What still needs setting up before tax can be calculated properly.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 border border-slate-600 text-slate-300 text-sm hover:border-amber-500 hover:text-amber-500"
        >
          <RefreshCw className="w-4 h-4" />
          Re-check
        </button>
      </div>

      {/* Headline */}
      <div
        className={`flex items-start gap-4 border p-5 ${
          report.ready
            ? 'bg-green-500/10 border-green-500/50'
            : 'bg-red-500/10 border-red-500/50'
        }`}
      >
        {report.ready ? (
          <CheckCircle2 className="w-7 h-7 text-green-400 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-7 h-7 text-red-400 flex-shrink-0" />
        )}
        <div>
          <h2
            className={`text-lg font-semibold ${report.ready ? 'text-green-400' : 'text-red-400'}`}
          >
            {report.ready
              ? 'Configuration is complete'
              : `${report.blockers} blocking issue${report.blockers === 1 ? '' : 's'}`}
          </h2>
          <p className="text-sm text-slate-300 mt-1">
            {report.ready
              ? 'Nothing is missing that would stop tax being calculated. That is not the same as the figures being right — see below.'
              : 'Tax raised on this instance will be wrong until these are fixed, and wrong silently rather than with an error.'}
            {report.warnings > 0 && ` ${report.warnings} warning${report.warnings === 1 ? '' : 's'} as well.`}
          </p>
        </div>
      </div>

      {/* WHAT THIS CANNOT TELL YOU. Always visible, never collapsed — a green
          check above must not be read as "my tax figures are correct". */}
      <div className="flex items-start gap-3 border border-amber-500/50 bg-amber-500/10 p-4">
        <ShieldQuestion className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-400 mb-2">What this check cannot tell you</p>
          <ul className="space-y-1.5 text-sm text-slate-300">
            {report.limitations.map((limitation) => (
              <li key={limitation} className="flex gap-2">
                <span className="text-amber-500/70">&bull;</span>
                <span>{limitation}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Things to fix */}
      {failed.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-slate-300 text-sm uppercase tracking-wider">Needs attention</h3>
          {failed
            .slice()
            .sort((a, b) => {
              const rank = { BLOCKER: 0, WARNING: 1, INFO: 2 } as const;
              return rank[a.severity] - rank[b.severity];
            })
            .map((check) => (
              <div
                key={check.id}
                className={`border p-4 ${
                  check.severity === 'BLOCKER'
                    ? 'border-red-500/50 bg-red-500/5'
                    : 'border-amber-500/40 bg-amber-500/5'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {check.severity === 'BLOCKER' ? (
                        <AlertCircle className="w-4 h-4 text-red-400" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                      )}
                      <span className="text-slate-100 font-medium">{check.title}</span>
                      <span
                        className={`px-2 py-0.5 text-xs border ${
                          check.severity === 'BLOCKER'
                            ? 'border-red-500/50 text-red-400'
                            : 'border-amber-500/50 text-amber-400'
                        }`}
                      >
                        {check.count}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mt-2">{check.consequence}</p>

                    {check.examples.length > 0 && (
                      <ul className="mt-2 space-y-0.5">
                        {check.examples.map((example) => (
                          <li key={example.id} className="text-xs text-slate-500 font-mono">
                            {example.label}
                            {example.detail && (
                              <span className="text-slate-600"> — {example.detail}</span>
                            )}
                          </li>
                        ))}
                        {check.count > check.examples.length && (
                          <li className="text-xs text-slate-600">
                            …and {check.count - check.examples.length} more
                          </li>
                        )}
                      </ul>
                    )}
                  </div>

                  <Link
                    to={check.fixPath}
                    className="flex-shrink-0 px-3 py-1.5 border border-amber-500 text-amber-500 text-sm hover:bg-amber-500/10 whitespace-nowrap"
                  >
                    {check.fixLabel} &rarr;
                  </Link>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Things that passed */}
      {passed.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-slate-300 text-sm uppercase tracking-wider">Passing</h3>
          <div className="bg-slate-800 border border-slate-700 divide-y divide-slate-700">
            {passed.map((check) => (
              <div key={check.id} className="flex items-center gap-3 px-4 py-2.5">
                <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span className="text-sm text-slate-300">{check.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-slate-600">
        <Info className="w-3 h-3" />
        Checked {new Date(report.generatedAt).toLocaleString()}
      </div>
    </div>
  );
}
